import { Injectable } from '@angular/core';
import { UserRole, WeldManagerUser } from 'src/app/core/models';
import { NotificationService } from 'src/app/layout/shared/notification';
import { UserInfoService } from './user-info.service';
import { WeldManagerUserApiService } from '../../core/api/weld-manager-user-api.service';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class WeldManagerUserService {
  public refineryId: number;
  public userId: string;
  constructor(
    private _srvWeldUsers: WeldManagerUserApiService,
    private _srvUserInfo: UserInfoService,
    private _srvNotify: NotificationService
  ) {}

  public returnName(userId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this._srvWeldUsers.GetUser(userId).subscribe({
        next: res => {
          resolve(Array.isArray(res) ? res[0] : res);
        },
        error: err => {
          reject(Error(err));
        }
      });
    });
  }

  public setparams(): void {
    const USER = JSON.parse(localStorage.getItem('user'));
    if (USER) {
      this.userId = USER.userId;
      this.refineryId = USER.refineryId;
    }
  }

  public getAllUsers(): void {
    this._srvWeldUsers.GetAllUsers().subscribe(res => {
      localStorage.removeItem('AllUsers');
      localStorage.setItem('AllUsers', JSON.stringify(res));
    });
  }

  public getUserName(userId: string): string {
    const DTO: any[] = JSON.parse(localStorage.getItem('AllUsers'));
    const user = DTO.find(u => u.userId === userId);
    return user ? user.firstName + ' ' + user.lastName : '';
  }

  public GetUserAsync(): Promise<WeldManagerUser[]> {
    return new Promise<WeldManagerUser[]>((resolve, reject) => {
      try {
        this._srvWeldUsers.GetUserAsync().subscribe(res => {
          resolve(res);
        });
      } catch (err) {
        reject(Error(err));
      }
    });
  }

  //the optional active param is only passed when false and both active and inactive users are pulled
  public GetUsersByRefineryAsync(refineryId: number, active?: boolean): Promise<WeldManagerUser[]> {
    return new Promise<WeldManagerUser[]>((resolve, reject) => {
      try {
        if (active) {
          this._srvWeldUsers.GetActiveUsersByRefineryAsync(refineryId).subscribe(res => {
            resolve(res);
          });
        } else {
          this._srvWeldUsers.GetAllUsersByRefineryAsync(refineryId, active).subscribe(res => {
            resolve(res);
          });
        }
      } catch (err) {
        reject(Error(err));
      }
    });
  }

  public GetUserRolesByUserAsync(userId: string): Promise<UserRole[]> {
    return new Promise<UserRole[]>((resolve, reject) => {
      try {
        this._srvWeldUsers.GetUserRolesByUserAsync(userId).subscribe({
          next: res => resolve(Array.isArray(res) ? res : res ? [res] : []),
          error: err => reject(err)
        });
      } catch (err) {
        reject(Error(err));
      }
    });
  }

  public GetAllUserRolesByUserAsync(userId: string): Promise<UserRole[]> {
    return new Promise<UserRole[]>((resolve, reject) => {
      try {
        this._srvWeldUsers.GetAllUserRolesByUserAsync(userId).subscribe({
          next: res => resolve(Array.isArray(res) ? res : res ? [res] : []),
          error: err => reject(err)
        });
      } catch (err) {
        reject(Error(err));
      }
    });
  }

  public GetUserRolesByRefineryAsync(refineryId: number, active?: boolean): Promise<UserRole[]> {
    return new Promise<UserRole[]>((resolve, reject) => {
      try {
        if (active == false) {
          this._srvWeldUsers.GetAllUserRolesByRefineryAsync(refineryId, active).subscribe({
            next: res => resolve(res),
            error: err => reject(err)
          });
        } else {
          this._srvWeldUsers.GetActiveUserRolesByRefineryAsync(refineryId).subscribe({
            next: res => resolve(res),
            error: err => reject(err)
          });
        }
      } catch (err) {
        reject(Error(err));
      }
    });
  }

  public async GetAvailableUserRolesByUserAsync(): Promise<UserRole[]> {
    this.setparams();
    const user: WeldManagerUser = await this.returnName(this.userId);
    const userRefineryId = Number(user?.refineryId);
    if (!Number.isInteger(userRefineryId) || userRefineryId <= 0) {
      throw new Error('The user record does not contain a valid refinery ID.');
    }
    const [availableRoles, assignments] = await Promise.all([
      firstValueFrom(this._srvWeldUsers.GetAvailableUserRolesByUserAsync(this.userId, userRefineryId)),
      this.GetAllUserRolesByUserAsync(this.userId).catch(() => null)
    ]);
    // If the active-assignment lookup fails, fall back to the available roles rather than blocking the view.
    if (!assignments) {
      return availableRoles;
    }
    const activeRoleIds = new Set(
      assignments
        .filter(assignment => Number(assignment.active) === 1 || String(assignment.active).toLowerCase() === 'true')
        .map(assignment => Number(assignment.roleId))
    );
    return availableRoles.filter(role => activeRoleIds.has(Number(role.roleId)));
  }

  public async AddorUpdateUserAsync(dto: any, existing?: boolean): Promise<string> {
    if (existing == null) {
      const persistedUserRoleId = dto.userRoleId ?? dto.UserRoleID ?? dto.userRoleID;
      existing = persistedUserRoleId != null;

      if (existing) {
        dto = { ...dto, userRoleId: persistedUserRoleId };
      } else {
        const userId = (dto.userId ?? dto.UserID)?.toString();
        const refineryId = Number(dto.refineryId);
        const userAssignments = await this.GetUserRolesByUserAsync(userId);
        const persistedAssignment = (userAssignments || []).find(
          assignment => Number((assignment as any).refineryId) === refineryId
        );

        existing = !!persistedAssignment;
        if (persistedAssignment) {
          const persistedData = persistedAssignment as any;
          dto = {
            ...persistedAssignment,
            ...dto,
            userRoleId: persistedData.userRoleId ?? persistedData.UserRoleID ?? persistedData.userRoleID
          };
        }
      }
    }

    const roleId = Number(dto.roleId);
    const refineryId = Number(dto.refineryId || this._srvUserInfo.refineryId);
    // New users added from AAD search have no default refinery yet; fall back to the refinery they are assigned to.
    const defaultRefineryId = Number.isFinite(Number(dto.defaultRefineryId))
      ? Number(dto.defaultRefineryId)
      : refineryId;
    if (!Number.isFinite(defaultRefineryId)) {
      throw new Error('The selected user does not have a valid default refinery.');
    }

    dto = {
      userRoleId: dto.userRoleId ?? 0,
      roleId,
      roleName: dto.roleName ?? null,
      defaultRoleId: Number(dto.defaultRoleId ?? roleId),
      userId: dto.userId,
      firstName: dto.firstName ?? null,
      lastName: dto.lastName ?? null,
      cai: dto.cai || '',
      emailId: dto.emailId ?? null,
      refineryId,
      refineryCode: dto.refineryCode ?? null,
      refineryName: dto.refineryName ?? null,
      defaultRefineryId,
      active: dto.active ?? true,
      reasonForDeletion: dto.reasonForDeletion || null,
      createdDateTime: dto.createdDateTime,
      createdBy: dto.createdBy ?? null,
      updatedDateTime: dto.updatedDateTime ?? null,
      updatedBy: dto.updatedBy ?? null
    };

    if (existing) {
      dto['updatedDateTime'] = new Date().toJSON();
      dto['updatedBy'] = this._srvUserInfo.userName;
    } else {
      dto['createdDateTime'] = new Date().toJSON();
      dto['createdBy'] = this._srvUserInfo.userName;
    }

    return new Promise<string>((resolve, reject) => {
      try {
        if (existing) {
          this._srvWeldUsers.UpdateUserAsync(dto).subscribe({
            next: res => resolve(res.toString()),
            error: err => reject(err)
          });
        } else {
          this._srvWeldUsers.AddUserAsync(dto).subscribe({
            next: res => resolve(res.toString()),
            error: err => reject(err)
          });
        }
      } catch (err) {
        reject(Error(err));
      }
    });
  }

  public SaveUserRoleAsync(dto: any, existing: boolean): Promise<string> {
    const roleId = Number(dto.roleId);
    // UserInfoService may have been constructed before the 'user' entry was written to localStorage.
    if (!this._srvUserInfo.userName) {
      this._srvUserInfo.loadUser();
    }
    const currentUser = this._srvUserInfo.userName || this._srvUserInfo.userId;
    const payload = {
      ...dto,
      userRoleId: Number(dto.userRoleId ?? dto.UserRoleID ?? dto.userRoleID ?? 0),
      roleId,
      defaultRoleId: Number(dto.defaultRoleId ?? roleId),
      refineryId: Number(dto.refineryId),
      defaultRefineryId: Number(dto.defaultRefineryId),
      createdDateTime: dto.createdDateTime ?? dto.CreatedDateTime ?? new Date().toJSON(),
      createdBy: dto.createdBy || dto.CreatedBy || currentUser,
      updatedDateTime: new Date().toJSON(),
      updatedBy: currentUser
    };

    return new Promise<string>((resolve, reject) => {
      const request = existing
        ? this._srvWeldUsers.UpdateUserAsync(payload, true)
        : this._srvWeldUsers.AddUserAsync(payload, true);
      request.subscribe({
        next: res => resolve(res.toString()),
        error: err => reject(err)
      });
    });
  }
}
